import { InboxOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Input,
  InputNumber,
  message,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Upload,
} from 'antd';
import { useMemo, useState } from 'react';

import { axiosClient } from '@/api/axiosClient';
import { useGetCustomerList } from '@/modules/customer/hooks/useGetCustomerList';
import { useGetFactoryList } from '@/modules/factory/hooks/useFactoryApi';

interface PayrollRow {
  id: string;
  worker_id: string;
  factory_id?: string;
  month: string;
  total_salary: number;
  total_allow: number;
  total_deduct: number;
  net: number;
  source: string;
}

interface PreviewRow {
  row: number;
  cccd?: string;
  worker_id?: string;
  full_name?: string;
  items?: { category: string; code: string; label: string; hours?: number; amount: number }[];
  net?: number;
  status: 'OK' | 'ERROR' | 'WARNING';
  error?: string;
}

interface PreviewResult {
  preview_id: string;
  filename: string;
  month: string;
  total_rows: number;
  ok_count: number;
  error_count: number;
  rows: PreviewRow[];
}

// T-TCKT-01..03 + V1.4 B4 — TC module. 3 tabs: bảng lương + import preview +
// settlement. The import flow follows the V1.4 B4 two-step preview/commit:
// upload returns a preview_id (cached 1h), TCKT eyeballs OK/Error/Warning,
// then clicks Commit to materialise the payrolls.
const PayrollPage = () => {
  const [tab, setTab] = useState('list');
  const [month, setMonth] = useState('');
  const [factoryId, setFactoryId] = useState('');

  const { data: payrolls, isLoading } = useQuery(
    ['payrolls', factoryId, month],
    () =>
      axiosClient.get<{ rows: PayrollRow[] }>(
        `/payroll/filter?factory_id=${factoryId}&month=${month}`
      ),
    { enabled: tab === 'list', refetchOnWindowFocus: false }
  );

  return (
    <Card title="Tài chính kế toán (T-TCKT)">
      <Tabs
        activeKey={tab}
        items={[
          {
            children: (
              <PayrollListTab
                factoryId={factoryId}
                month={month}
                onFactoryChange={setFactoryId}
                onMonthChange={setMonth}
                onSwitchToImport={() => setTab('import')}
                rows={
                  (payrolls as unknown as { data?: { rows: PayrollRow[] } })?.data?.rows || []
                }
                loading={isLoading}
              />
            ),
            key: 'list',
            label: 'Bảng lương',
          },
          { children: <PayrollImportTab />, key: 'import', label: 'Import lương (preview/commit)' },
          { children: <ReconcileTab />, key: 'reconcile', label: 'Đối soát công (T-VH-08)' },
          { children: <SettlementTab />, key: 'settlement', label: 'Quyết toán KH' },
        ]}
        onChange={setTab}
      />
    </Card>
  );
};

const PayrollListTab = ({
  rows,
  loading,
  factoryId,
  month,
  onFactoryChange,
  onMonthChange,
  onSwitchToImport,
}: {
  rows: PayrollRow[];
  loading: boolean;
  factoryId: string;
  month: string;
  onFactoryChange: (v: string) => void;
  onMonthChange: (v: string) => void;
  // QC #34 — let the empty list jump straight to the Import tab so users
  // who land on "Bảng lương" don't have to guess that the Import lever
  // lives one tab over.
  onSwitchToImport: () => void;
}) => {
  const columns = [
    { dataIndex: 'worker_id', key: 'worker', render: (v: string) => v.slice(0, 8) + '…', title: 'NLĐ' },
    { dataIndex: 'month', key: 'month', title: 'Tháng' },
    { dataIndex: 'total_salary', key: 'sal', render: (v: number) => v.toLocaleString('vi-VN'), title: 'Lương' },
    { dataIndex: 'total_allow', key: 'allow', render: (v: number) => v.toLocaleString('vi-VN'), title: 'Phụ cấp' },
    { dataIndex: 'total_deduct', key: 'ded', render: (v: number) => v.toLocaleString('vi-VN'), title: 'Giảm trừ' },
    { dataIndex: 'net', key: 'net', render: (v: number) => <strong>{v.toLocaleString('vi-VN')}đ</strong>, title: 'Thực nhận' },
    { dataIndex: 'source', key: 'src', render: (v: string) => <Tag>{v}</Tag>, title: 'Nguồn' },
  ];

  // V1.4 D4 — replace free-text Factory ID with a dropdown. Loaded once at
  // tab mount; pageSize=200 covers every demo customer + leaves headroom.
  const { data: factories } = useGetFactoryList({ limit: 200, page: 1 });
  const factoryOptions = useMemo(
    () =>
      ((factories?.rows as Array<{ id?: string; name: string }>) ?? [])
        .filter((f) => f.id)
        .map((f) => ({ label: f.name, value: f.id! })),
    [factories]
  );

  return (
    <>
      <Space className="mb-3" style={{ width: '100%', justifyContent: 'space-between' }}>
        <Space>
          <Select
            allowClear
            onChange={(v) => onFactoryChange(v || '')}
            optionFilterProp="label"
            options={factoryOptions}
            placeholder="Chọn nhà máy"
            showSearch
            style={{ width: 240 }}
            value={factoryId || undefined}
          />
          <Input
            onChange={(e) => onMonthChange(e.target.value)}
            placeholder="YYYY-MM"
            value={month}
          />
        </Space>
        <Button onClick={onSwitchToImport} type="primary">
          + Import bảng lương
        </Button>
      </Space>
      <Table<PayrollRow>
        columns={columns}
        dataSource={rows}
        loading={loading}
        // QC #34 — empty state used to be silent; users thought page was
        // broken. Surface the actual workflow (Import lương → preview →
        // commit) so the user knows where to add data.
        locale={{
          emptyText: (
            <div style={{ padding: 24 }}>
              <p>
                <strong>Chưa có bảng lương nào</strong>
              </p>
              <p style={{ color: '#999' }}>
                Để thêm bảng lương: chuyển tab <strong>"Import lương"</strong> phía
                trên → upload file Excel theo FORM IMPORT MẪU → kiểm tra Preview →
                Commit.
              </p>
            </div>
          ),
        }}
        rowKey="id"
        size="middle"
      />
    </>
  );
};

const PayrollImportTab = () => {
  const [preview, setPreview] = useState<PreviewResult | null>(null);

  // V1.4 D3.3 — switched from client-side sheetjs parse + POST JSON to
  // multipart upload to /payroll/preview-upload (D3.2). The BE owns parsing
  // and validation now; FE just hands over the file. Smaller bundle (no
  // sheetjs import) + a single source of truth for column-mapping.
  const previewMutation = useMutation(
    (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('month', monthInput);

      return axiosClient.post<PreviewResult>('/payroll/preview-upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    {
      onSuccess: (res) => {
        setPreview(res as unknown as PreviewResult);
        message.success('Parse file thành công, kiểm tra tab OK/Lỗi trước khi commit');
      },
      onError: (err: unknown) => {
        // QC #35 — surface the BE envelope's actual error_info.message so
        // users see "Cột CCCD thiếu ở dòng 12" instead of a generic
        // "Lỗi parse file". axiosClient throws AppError; some BE paths
        // also bubble plain Error.
        const e = err as { message?: string; debug_message?: string };
        message.error(e?.message || e?.debug_message || 'Lỗi parse file');
      },
    }
  );
  const commitMutation = useMutation(
    (id: string) => axiosClient.post(`/payroll/commit/${id}`),
    {
      onSuccess: () => {
        message.success('Đã commit bảng lương');
        setPreview(null);
      },
    }
  );

  const [monthInput, setMonthInput] = useState('');

  function handleFileUpload(file: File) {
    if (!monthInput) {
      message.warning('Vui lòng nhập tháng (YYYY-MM) trước khi upload');

      return false;
    }
    previewMutation.mutate(file);

    return false; // prevent antd auto-upload
  }

  const okRows = preview?.rows.filter((r) => r.status === 'OK') || [];
  const errRows = preview?.rows.filter((r) => r.status === 'ERROR') || [];
  const warnRows = preview?.rows.filter((r) => r.status === 'WARNING') || [];

  return (
    <>
      {!preview && (
        <>
          <Alert
            description="V1.4 B4 — Upload file Excel theo FORM IMPORT MẪU (Phần I §8.1). Cột headers: STT / Họ tên / CCCD / Tổng công / [Lương ca ngày/đêm + TC/CN/lễ] / [Phụ cấp xăng xe/môi trường/thâm niên/chuyên cần] / [Giảm trừ ứng lương/ý thức/đồng phục/TT lưu động]. BE parse + validate + cache preview 1h trước commit."
            message="Bước 1: Preview"
            showIcon
            type="info"
          />
          <Space className="mt-3" direction="vertical" style={{ width: '100%' }}>
            <Input
              onChange={(e) => setMonthInput(e.target.value)}
              placeholder="Tháng lương (YYYY-MM, vd 2026-05)"
              style={{ maxWidth: 240 }}
              value={monthInput}
            />
            <Upload.Dragger
              accept=".xlsx,.xls"
              beforeUpload={handleFileUpload}
              disabled={previewMutation.isLoading || !monthInput}
              maxCount={1}
              showUploadList={false}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined rev="" />
              </p>
              <p className="ant-upload-text">Kéo thả file Excel vào đây, hoặc click để chọn</p>
              <p className="ant-upload-hint">
                Hỗ trợ .xlsx / .xls. Tham khảo FORM IMPORT MẪU PJOS để có template chuẩn.
              </p>
            </Upload.Dragger>
          </Space>
        </>
      )}

      {preview && (
        <>
          <Alert
            description={`File: ${preview.filename} | Tháng: ${preview.month} | OK: ${preview.ok_count} | Lỗi: ${preview.error_count}`}
            message={`Preview ID: ${preview.preview_id}`}
            showIcon
            type="info"
          />
          <Tabs
            className="mt-3"
            items={[
              {
                children: (
                  <Table
                    columns={[
                      { dataIndex: 'row', title: '#', width: 50 },
                      { dataIndex: 'cccd', title: 'CCCD' },
                      { dataIndex: 'full_name', title: 'Họ tên' },
                      { dataIndex: 'net', render: (v: number) => v?.toLocaleString('vi-VN'), title: 'Net' },
                    ]}
                    dataSource={okRows}
                    rowKey="row"
                    size="small"
                  />
                ),
                key: 'ok',
                label: `OK (${okRows.length})`,
              },
              {
                children: (
                  <Table
                    columns={[
                      { dataIndex: 'row', title: '#', width: 50 },
                      { dataIndex: 'cccd', title: 'CCCD' },
                      { dataIndex: 'error', render: (v: string) => <span className="text-danger">{v}</span>, title: 'Lỗi' },
                    ]}
                    dataSource={errRows}
                    rowKey="row"
                    size="small"
                  />
                ),
                key: 'err',
                label: `Lỗi (${errRows.length})`,
              },
              {
                children: (
                  <Table
                    columns={[
                      { dataIndex: 'row', title: '#', width: 50 },
                      { dataIndex: 'error', title: 'Cảnh báo' },
                    ]}
                    dataSource={warnRows}
                    rowKey="row"
                    size="small"
                  />
                ),
                key: 'warn',
                label: `Cảnh báo (${warnRows.length})`,
              },
            ]}
          />
          <Space className="mt-3">
            <Button
              disabled={preview.ok_count === 0}
              loading={commitMutation.isLoading}
              onClick={() => commitMutation.mutate(preview.preview_id)}
              type="primary"
            >
              Commit {preview.ok_count} dòng
            </Button>
            <Button onClick={() => setPreview(null)}>Huỷ</Button>
          </Space>
        </>
      )}
    </>
  );
};

// --- T-VH-08 Đối soát công chuẩn cuối tháng (HCNS) --------------------------
// Mô hình PO 2026-09-07: DATA A (NLĐ tự khai) vs DATA B (QLĐ khai) → HCNS
// đối chiếu, duyệt Công chuẩn per-day (Attendance Final, lưu riêng không ghi
// đè A/B) → chốt (immutable, gắn Factory) → Export Excel = ĐẦU RA CUỐI V1.
// Q1: ngày công = (HC ngày + HC đêm)/8, giữ số lẻ, KHÔNG round.
// Q6/Q31: TC ngày (ngày thường) = TC_NGAY + TC_TRUA; Chủ nhật → CN ngày/đêm.
// Q10: ngày không có bản ghi nào = "thiếu dữ liệu" (hiện "—"), không auto KP.
// Q28: bản ghi đi làm không có chi tiết ca chỉ là THAM CHIẾU (đếm 1 công) —
// Final do HCNS duyệt từng ngày. Q35: dòng is_system không tính vào DATA B.

// E2 — dữ liệu 2 nguồn lấy từ /payroll/reconcile/aggregate (tổng hợp
// server-side theo worker × ngày, kèm info NLĐ) thay vì kéo raw rows với
// limit cứng — hết rủi ro cắt im lặng khi scale.
interface AggDayRow {
  day: string;
  has_shift: boolean;
  hc_dem: number;
  hc_ngay: number;
  present: boolean;
  tc_dem: number;
  tc_ngay: number;
  tc_trua: number;
  worker_id: string;
}

interface AggWorkerRow {
  full_name: string;
  id: string;
  id_number: string;
}

interface AttendanceAggregate {
  a?: AggDayRow[];
  b?: AggDayRow[];
  workers?: AggWorkerRow[];
}

interface ShiftTotals {
  cnDem: number;
  cnNgay: number;
  hcDem: number;
  hcNgay: number;
  tcDem: number;
  tcNgay: number;
}

interface SourceAgg {
  days: Map<string, number>;
  has: Set<string>;
  totals: ShiftTotals;
}

interface ReconcileRow {
  detail: { day: string; diff: boolean; hasA: boolean; hasB: boolean; nld: number; qld: number }[];
  mismatch: number;
  name: string;
  nldTotals: ShiftTotals;
  nld_total: number;
  qldTotals: ShiftTotals;
  qld_total: number;
  worker_id: string;
}

interface MonthlyAttendanceRow {
  day_count: number;
  id: string;
  month: string;
  source_type: string;
  worker_id: string;
  factory_id?: string;
  snapshot_json?: string;
}

interface SnapshotDays {
  days?: Record<string, { final?: number; nld?: number; qld?: number }>;
}

function emptyTotals(): ShiftTotals {
  return { cnDem: 0, cnNgay: 0, hcDem: 0, hcNgay: 0, tcDem: 0, tcNgay: 0 };
}

// Công của 1 ô (worker × ngày): có chi tiết ca → (HC ngày + HC đêm)/8 giữ lẻ
// (Q1); không có ca nhưng có bản ghi đi làm → 1 công THAM CHIẾU (Q28 — Final
// do HCNS duyệt per-day).
function dayCountOf(d: AggDayRow): number {
  if (d.has_shift) return (d.hc_ngay + d.hc_dem) / 8;

  return d.present ? 1 : 0;
}

// Cộng giờ theo loại (Q6/Q31): ngày thường TC ngày gộp TC_TRUA; Chủ nhật đổ
// TC sang CN ngày/đêm (TC_TRUA ngày CN tạm vào CN ngày — Q7 còn OPEN).
function accumulate(t: ShiftTotals, d: AggDayRow) {
  const isSunday = new Date(d.day).getDay() === 0;
  t.hcNgay += d.hc_ngay;
  t.hcDem += d.hc_dem;
  if (isSunday) {
    t.cnNgay += d.tc_ngay + d.tc_trua;
    t.cnDem += d.tc_dem;
  } else {
    t.tcNgay += d.tc_ngay + d.tc_trua;
    t.tcDem += d.tc_dem;
  }
}

function buildAgg(rows: AggDayRow[]): Map<string, SourceAgg> {
  const m = new Map<string, SourceAgg>();
  rows.forEach((d) => {
    const agg = m.get(d.worker_id) ?? { days: new Map(), has: new Set(), totals: emptyTotals() };
    agg.days.set(d.day, (agg.days.get(d.day) || 0) + dayCountOf(d));
    agg.has.add(d.day);
    accumulate(agg.totals, d);
    m.set(d.worker_id, agg);
  });

  return m;
}

function daysInMonth(month: string): string[] {
  const total = new Date(+month.slice(0, 4), +month.slice(5, 7), 0).getDate();

  return Array.from({ length: total }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
}

function parseSnapshotDays(row?: MonthlyAttendanceRow): SnapshotDays {
  if (!row?.snapshot_json) return {};
  try {
    return JSON.parse(row.snapshot_json) as SnapshotDays;
  } catch {
    return {};
  }
}

const ReconcileTab = () => {
  const [month, setMonth] = useState('');
  const [factoryId, setFactoryId] = useState('');

  // Nguồn prefill per NLĐ (QLD default). Edit tay bất kỳ ô Final nào →
  // source_type gửi lên tự thành MIXED (derive lúc chốt).
  const [choices, setChoices] = useState<Record<string, { source: string }>>({});

  // Per-day Final HCNS đã sửa tay: dayFinals[worker_id][YYYY-MM-DD].
  const [dayFinals, setDayFinals] = useState<Record<string, Record<string, number>>>({});

  const validMonth = /^\d{4}-\d{2}$/.test(month);

  const { data: factories } = useGetFactoryList({ limit: 200, page: 1 });
  const factoryOptions = useMemo(
    () =>
      ((factories?.rows as Array<{ name: string; id?: string }>) ?? [])
        .filter((f) => f.id)
        .map((f) => ({ label: f.name, value: f.id! })),
    [factories]
  );

  const { data: aggRes, isLoading: loadingAgg } = useQuery(
    ['reconcile-agg', month],
    () => axiosClient.get(`/payroll/reconcile/aggregate?month=${month}`),
    { enabled: validMonth, refetchOnWindowFocus: false }
  );
  const agg = (aggRes as unknown as { data?: AttendanceAggregate })?.data;
  const { data: lockedRes, refetch: refetchLocked } = useQuery(
    ['reconcile-locked', month],
    () => axiosClient.get(`/payroll/reconcile/filter?month=${month}&page=1&limit=1000`),
    { enabled: validMonth, refetchOnWindowFocus: false }
  );

  const workerInfo = useMemo(
    () =>
      new Map(
        (agg?.workers ?? []).map((w) => [
          w.id,
          { cccd: w.id_number || '', name: w.full_name || '' },
        ])
      ),
    [agg]
  );

  const nameOf = (id: string) => workerInfo.get(id)?.name || `${id.slice(0, 8)}…`;
  const cccdOf = (id: string) => workerInfo.get(id)?.cccd || '';

  const rows: ReconcileRow[] = useMemo(() => {
    if (!validMonth) return [];
    const nld = buildAgg(agg?.a ?? []);
    const qld = buildAgg(agg?.b ?? []);
    const days = daysInMonth(month);
    const ids = new Set([...nld.keys(), ...qld.keys()]);

    return [...ids]
      .map((wid) => {
        const a = nld.get(wid);
        const b = qld.get(wid);
        let nldTotal = 0;
        let qldTotal = 0;
        let mismatch = 0;
        const detail = days.map((d) => {
          const hasA = a?.has.has(d) ?? false;
          const hasB = b?.has.has(d) ?? false;
          const x = a?.days.get(d) ?? 0;
          const y = b?.days.get(d) ?? 0;
          nldTotal += x;
          qldTotal += y;
          const diff = (hasA || hasB) && x !== y;
          if (diff) mismatch += 1;

          return { day: d, diff, hasA, hasB, nld: x, qld: y };
        });

        return {
          detail,
          mismatch,
          name: nameOf(wid),
          nldTotals: a?.totals ?? emptyTotals(),
          nld_total: nldTotal,
          qldTotals: b?.totals ?? emptyTotals(),
          qld_total: qldTotal,
          worker_id: wid,
        };
      })
      .sort((x, y) => y.mismatch - x.mismatch);
  }, [agg, month, validMonth, workerInfo]);

  const lockedByWorker = useMemo(() => {
    const lockedRows =
      (lockedRes as unknown as { data?: { rows?: MonthlyAttendanceRow[] } })?.data?.rows ?? [];

    return new Map(lockedRows.map((r) => [r.worker_id, r]));
  }, [lockedRes]);

  // Final của 1 ngày = edit tay nếu có, ngược lại theo nguồn prefill.
  function finalOf(row: ReconcileRow, day: { day: string; nld: number; qld: number }): number {
    const manual = dayFinals[row.worker_id]?.[day.day];
    if (manual !== undefined) return manual;
    const src = choices[row.worker_id]?.source ?? 'QLD';

    return src === 'NLD' ? day.nld : day.qld;
  }

  function finalTotalOf(row: ReconcileRow): number {
    return row.detail.reduce((acc, d) => acc + finalOf(row, d), 0);
  }

  const lockMutation = useMutation(
    (payload: {
      days: Record<string, { final: number; nld: number; qld: number }>;
      factory_id: string;
      month: string;
      snapshot: unknown;
      source_type: string;
      worker_id: string;
    }) => axiosClient.post('/payroll/reconcile', payload),
    {
      onError: (err: unknown) => {
        message.error((err as { message?: string })?.message || 'Chốt công thất bại');

        // 409 = đã chốt ở phiên khác — đồng bộ lại trạng thái khoá.
        refetchLocked();
      },
      onSuccess: () => {
        message.success('Đã chốt công chuẩn (immutable)');
        refetchLocked();
      },
    }
  );

  function handleLock(row: ReconcileRow) {
    if (!factoryId) {
      message.warning('Chọn Nhà máy trước khi chốt — công chuẩn phải gắn Factory (Q25)');

      return;
    }
    const days: Record<string, { final: number; nld: number; qld: number }> = {};
    let allNld = true;
    let allQld = true;
    row.detail.forEach((d) => {
      const final = finalOf(row, d);
      if (final !== d.nld) allNld = false;
      if (final !== d.qld) allQld = false;
      days[d.day] = { final, nld: d.nld, qld: d.qld };
    });
    lockMutation.mutate({
      days,
      factory_id: factoryId,
      month,
      snapshot: {
        mismatch_days: row.mismatch,
        nld_total: row.nld_total,
        qld_total: row.qld_total,
      },
      source_type: allNld ? 'NLD' : allQld ? 'QLD' : 'MIXED',
      worker_id: row.worker_id,
    });
  }

  // Q34/Q39 — Export Excel = ĐẦU RA CUỐI V1: 1 workbook 3 sheet
  // (DATA A / DATA B / FINAL), đủ NLĐ + CCCD + Factory + per-day + tổng +
  // loại công. Dynamic import giữ sheetjs ngoài initial bundle.
  async function handleExport() {
    const XLSX = await import('xlsx');
    const days = daysInMonth(month);
    const dayHeads = days.map((d) => d.slice(8));
    const factoryName = factoryOptions.find((f) => f.value === factoryId)?.label || '(chưa chọn)';
    const meta = [`Tháng: ${month}`, `Nhà máy: ${factoryName}`];
    const wb = XLSX.utils.book_new();

    const srcHeader = [
      'STT',
      'Họ tên',
      'CCCD',
      'Mã NLĐ',
      ...dayHeads,
      'Tổng ngày công',
      'HC ngày (h)',
      'HC đêm (h)',
      'TC ngày (h, gồm trưa, trừ CN)',
      'TC đêm (h, trừ CN)',
      'CN ngày (h)',
      'CN đêm (h)',
    ];

    (['NLD', 'QLD'] as const).forEach((src) => {
      const body = rows.map((r, i) => {
        const t = src === 'NLD' ? r.nldTotals : r.qldTotals;

        return [
          i + 1,
          r.name,
          cccdOf(r.worker_id),
          r.worker_id,
          ...r.detail.map((d) => {
            const has = src === 'NLD' ? d.hasA : d.hasB;

            return has ? (src === 'NLD' ? d.nld : d.qld) : '';
          }),
          src === 'NLD' ? r.nld_total : r.qld_total,
          t.hcNgay,
          t.hcDem,
          t.tcNgay,
          t.tcDem,
          t.cnNgay,
          t.cnDem,
        ];
      });
      const ws = XLSX.utils.aoa_to_sheet([meta, srcHeader, ...body]);
      XLSX.utils.book_append_sheet(
        wb,
        ws,
        src === 'NLD' ? 'DATA A (NLD khai)' : 'DATA B (QLD khai)'
      );
    });

    const finalHeader = [
      'STT',
      'Họ tên',
      'CCCD',
      'Mã NLĐ',
      'Factory',
      'Nguồn',
      ...dayHeads,
      'Tổng công chuẩn',
    ];
    const finalBody = [...lockedByWorker.values()].map((r, i) => {
      const snap = parseSnapshotDays(r);

      return [
        i + 1,
        nameOf(r.worker_id),
        cccdOf(r.worker_id),
        r.worker_id,
        r.factory_id ?? '',
        r.source_type,
        ...days.map((d) => snap.days?.[d]?.final ?? ''),
        r.day_count,
      ];
    });
    const wsFinal = XLSX.utils.aoa_to_sheet([meta, finalHeader, ...finalBody]);
    XLSX.utils.book_append_sheet(wb, wsFinal, 'FINAL (Cong chuan)');

    XLSX.writeFile(wb, `cong-v1-${month}.xlsx`);
  }

  // Bảng mở rộng: đủ 31 ô/ngày; "—" = thiếu dữ liệu (Q10); cột Công chuẩn
  // editable khi chưa chốt.
  function expandedRowRender(row: ReconcileRow) {
    const locked = lockedByWorker.get(row.worker_id);
    const snap = parseSnapshotDays(locked);
    const cell = (v: number, has: boolean) => (has ? +v.toFixed(3) : '—');

    return (
      <Table
        columns={[
          { dataIndex: 'day', key: 'd', render: (v: string) => v.slice(8), title: 'Ngày' },
          {
            key: 'n',
            render: (_: unknown, d: { hasA: boolean; nld: number }) => cell(d.nld, d.hasA),
            title: 'DATA A (NLĐ khai)',
          },
          {
            key: 'q',
            render: (_: unknown, d: { hasB: boolean; qld: number }) => cell(d.qld, d.hasB),
            title: 'DATA B (QLĐ khai)',
          },
          {
            key: 'f',
            render: (_: unknown, d: { day: string; nld: number; qld: number }) =>
              locked ? (
                <strong>{snap.days?.[d.day]?.final ?? '—'}</strong>
              ) : (
                <InputNumber
                  min={0}
                  onChange={(v) =>
                    setDayFinals((prev) => ({
                      ...prev,
                      [row.worker_id]: {
                        ...(prev[row.worker_id] ?? {}),
                        [d.day]: v ?? 0,
                      },
                    }))
                  }
                  size="small"
                  step={0.5}
                  style={{ width: 80 }}
                  value={finalOf(row, d)}
                />
              ),
            title: 'Công chuẩn (Final)',
          },
        ]}
        dataSource={row.detail}
        pagination={false}
        rowClassName={(d) => ((d as { diff: boolean }).diff ? 'table-row-danger' : '')}
        rowKey="day"
        scroll={{ y: 320 }}
        size="small"
      />
    );
  }

  const columns = [
    { dataIndex: 'name', key: 'name', title: 'NLĐ' },
    {
      dataIndex: 'nld_total',
      key: 'nld',
      render: (v: number) => +v.toFixed(3),
      title: 'DATA A (công)',
    },
    {
      dataIndex: 'qld_total',
      key: 'qld',
      render: (v: number) => +v.toFixed(3),
      title: 'DATA B (công)',
    },
    {
      dataIndex: 'mismatch',
      key: 'mismatch',
      render: (v: number) =>
        v > 0 ? <Tag color="red">{v} ngày lệch</Tag> : <Tag color="green">Khớp</Tag>,
      title: 'Đối soát',
    },
    {
      key: 'final',
      render: (_: unknown, row: ReconcileRow) => {
        const locked = lockedByWorker.get(row.worker_id);

        return locked ? (
          <strong>{locked.day_count}</strong>
        ) : (
          <span>{+finalTotalOf(row).toFixed(3)}</span>
        );
      },
      title: 'Công chuẩn (Σ Final)',
    },
    {
      key: 'lock',
      render: (_: unknown, row: ReconcileRow) => {
        const locked = lockedByWorker.get(row.worker_id);

        // Đã chốt = read-only (immutable — PO Q24 còn OPEN việc mở khoá).
        if (locked) {
          return (
            <Tag color="blue">
              Đã chốt: {locked.day_count} công ({locked.source_type}) — read-only
            </Tag>
          );
        }
        const choice = choices[row.worker_id] ?? { source: 'QLD' };

        return (
          <Space>
            <Select
              onChange={(v) => {
                setChoices((prev) => ({ ...prev, [row.worker_id]: { source: v } }));

                // đổi nguồn prefill → bỏ các edit tay cũ của NLĐ này
                setDayFinals((prev) => {
                  const next = { ...prev };
                  delete next[row.worker_id];

                  return next;
                });
              }}
              options={[
                { label: 'Prefill theo DATA B', value: 'QLD' },
                { label: 'Prefill theo DATA A', value: 'NLD' },
              ]}
              size="small"
              style={{ width: 170 }}
              value={choice.source}
            />
            <Button
              disabled={!factoryId}
              loading={lockMutation.isLoading}
              onClick={() => handleLock(row)}
              size="small"
              type="primary"
            >
              Chốt
            </Button>
          </Space>
        );
      },
      title: 'Chốt công chuẩn',
    },
  ];

  return (
    <>
      <Alert
        description="DATA A (NLĐ tự khai) vs DATA B (QLĐ khai) — hai nguồn lưu độc lập, không ghi đè. Ngày công = (HC ngày + HC đêm)/8, giữ số lẻ (Q1); ô '—' = thiếu dữ liệu, không auto KP (Q10). Mở rộng dòng để duyệt Công chuẩn từng ngày → Chốt (bắt buộc gắn Nhà máy, immutable) → Export Excel là đầu ra cuối của V1."
        message="Đối soát công chuẩn cuối tháng (HCNS)"
        type="info"
        showIcon
      />
      <Space className="mt-3 mb-3" wrap>
        <Input
          onChange={(e) => setMonth(e.target.value)}
          placeholder="Tháng (YYYY-MM)"
          style={{ width: 160 }}
          value={month}
        />
        <Select
          onChange={(v) => setFactoryId(v || '')}
          optionFilterProp="label"
          options={factoryOptions}
          placeholder="Nhà máy (bắt buộc khi chốt)"
          status={factoryId ? '' : 'warning'}
          style={{ width: 260 }}
          value={factoryId || undefined}
          allowClear
          showSearch
        />
        <Button
          disabled={!validMonth || (!rows.length && lockedByWorker.size === 0)}
          onClick={() => handleExport()}
          type="primary"
        >
          Export Excel (DATA A / DATA B / FINAL)
        </Button>
      </Space>
      <Table<ReconcileRow>
        columns={columns}
        dataSource={rows}
        expandable={{ expandedRowRender }}
        loading={loadingAgg}
        locale={{
          emptyText: validMonth
            ? 'Không có dữ liệu chấm công trong tháng này.'
            : 'Nhập tháng (YYYY-MM) để bắt đầu đối soát.',
        }}
        rowKey="worker_id"
        size="middle"
      />
      {/* Ngày lệch trong bảng con: tô đỏ qua class antd row */}
      <style>{`.table-row-danger td { background: #fff1f0 !important; }`}</style>
    </>
  );
};

interface ComputePayload {
  customer_id: string;
  month: string;
  service_group: string;
  total_hours: number;
}

function computeSettlement(payload: ComputePayload) {
  return axiosClient.post<{ id: string }>('/payroll/settlement/compute', payload);
}

const SettlementTab = () => {
  const [customerId, setCustomerId] = useState('');
  const [month, setMonth] = useState('');
  const [serviceGroup, setServiceGroup] = useState<string>('RENT_LABOR');
  const [totalHours, setTotalHours] = useState<string>('');

  // V1.4 D4 — Customer dropdown + serviceGroup Select replace the
  // getElementById hack + free-text inputs.
  const { data: customers } = useGetCustomerList('page=1&limit=200');
  const customerOptions = useMemo(
    () =>
      ((customers as unknown as { rows?: Array<{ id?: string; name: string }> })?.rows ?? [])
        .filter((c) => c.id)
        .map((c) => ({ label: c.name, value: c.id! })),
    [customers]
  );

  // QC #36 — historical settlements so admin sees what's already been
  // computed (the page used to show only the input form → looked broken
  // when no data existed).
  const { data: settlements, isLoading: loadingSettlements } = useQuery(
    ['settlement-list'],
    () => axiosClient.get('/payroll/settlement/filter?page=1&limit=20'),
    { refetchOnWindowFocus: false }
  );
  const settlementRows =
    (settlements as unknown as { data?: { rows?: Array<{ id: string; customer_id: string; month: string; service_group: string; total_hours: number; total_amount: number; computed_at: string }> } })?.data?.rows ?? [];

  // @ts-expect-error TS overload resolution bug in @tanstack/react-query v4
  // — picks overload 3 (mutationKey, ...) instead of 2 (mutationFn, options)
  // for this particular call. Runtime is fine; pattern matches previewMutation
  // above which type-checks happily. Not worth a refactor for a one-liner.
  const computeMutation = useMutation(computeSettlement, {
    onError: (err: unknown) => {
      const m = (err as { message?: string })?.message ?? 'Tính quyết toán thất bại';
      message.error(m);
    },
    onSuccess: () => message.success('Đã tính & lưu quyết toán'),
  });

  return (
    <Card title="Quyết toán tiền KH (T-TCKT-03 + V1.4 A1)">
      <Alert
        description="Formula §7.5: RENT_LABOR = giờ × 30k. JOB_MATCHING = giờ × 5k cho NLĐ < 3 tháng đầu. Dồn per Customer × tháng (gộp tất cả Factories)."
        message="Công thức"
        showIcon
        type="info"
      />
      <Space className="mt-3" wrap>
        <Select
          allowClear
          onChange={(v) => setCustomerId(v || '')}
          optionFilterProp="label"
          options={customerOptions}
          placeholder="Khách hàng"
          showSearch
          style={{ width: 220 }}
          value={customerId || undefined}
        />
        <Input
          onChange={(e) => setMonth(e.target.value)}
          placeholder="YYYY-MM"
          style={{ width: 140 }}
          value={month}
        />
        <Select
          onChange={setServiceGroup}
          options={[
            { label: 'Cho thuê NLĐ (RENT_LABOR)', value: 'RENT_LABOR' },
            { label: 'Tuyển hộ (JOB_MATCHING)', value: 'JOB_MATCHING' },
          ]}
          style={{ width: 240 }}
          value={serviceGroup}
        />
        <Input
          onChange={(e) => setTotalHours(e.target.value)}
          placeholder="Tổng giờ"
          style={{ width: 140 }}
          value={totalHours}
        />
        <Button
          disabled={!customerId || !month}
          loading={computeMutation.isLoading}
          onClick={() =>
            computeMutation.mutate({
              customer_id: customerId,
              month,
              service_group: serviceGroup,
              total_hours: parseFloat(totalHours || '0'),
            })
          }
          type="primary"
        >
          Tính & Lưu
        </Button>
      </Space>

      {/* QC #36 — historical settlements table */}
      <Table
        className="mt-4"
        columns={[
          { dataIndex: 'customer_id', key: 'c', render: (v: string) => v?.slice(0, 8) + '…', title: 'KH' },
          { dataIndex: 'month', key: 'm', title: 'Tháng' },
          {
            dataIndex: 'service_group',
            key: 'sg',
            render: (v: string) => <Tag>{v}</Tag>,
            title: 'Loại',
          },
          { dataIndex: 'total_hours', key: 'h', title: 'Tổng giờ' },
          {
            dataIndex: 'total_amount',
            key: 'a',
            render: (v: number) => <strong>{v?.toLocaleString('vi-VN')}đ</strong>,
            title: 'Số tiền',
          },
          { dataIndex: 'computed_at', key: 'd', title: 'Ngày tính' },
        ]}
        dataSource={settlementRows}
        loading={loadingSettlements}
        locale={{
          emptyText: 'Chưa có quyết toán nào — điền form phía trên + bấm "Tính & Lưu".',
        }}
        rowKey="id"
        size="small"
      />
    </Card>
  );
};

export default PayrollPage;
